import React from "react";

const PrivacyPolicy: React.FC = () => {
  const useScrollToTop = () => {
    React.useEffect(() => {
      window.scrollTo(0, 0);
    }, []);
  }
  useScrollToTop();

  const sections = [
    { id: "information-collection", title: "Information We Collect" },
    { id: "information-use", title: "How We Use Your Information" },
    { id: "information-sharing", title: "Sharing of Information" },
    { id: "data-security", title: "Data Security" },
    { id: "cookies", title: "Cookies and Tracking" },
    { id: "third-party", title: "Third-Party Services" },
    { id: "data-retention", title: "Data Retention" },
    { id: "international-transfers", title: "International Data Transfers" },
    { id: "children-privacy", title: "Children's Privacy" },
    { id: "your-rights", title: "Your Privacy Rights" },
    { id: "policy-changes", title: "Changes to This Policy" },
    { id: "contact", title: "Contact Us" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header Section */}
      <div className="bg-white shadow-lg border-b border-gray-200">
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Privacy Policy
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Your privacy matters to us. Learn how we collect, use, and protect your personal information.
            </p>
            <div className="mt-6 flex items-center justify-center space-x-4 text-sm text-gray-500">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                </svg>
                Last Updated: May 20, 2025
              </span>
              <span>•</span>
              <span>Effective Date: May 20, 2025</span>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-12">
            {/* Table of Contents */}
            <div className="lg:col-span-1">
              <div className="sticky top-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h3>
                  <nav className="space-y-2">
                    {sections.map((section, index) => (
                      <a
                        key={section.id}
                        href={`#${section.id}`}
                        className="block text-sm text-gray-600 hover:text-blue-600 transition-colors duration-200 py-1 border-l-2 border-transparent hover:border-blue-600 pl-3"
                      >
                        {index + 1}. {section.title}
                      </a>
                    ))}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                <div className="p-8 md:p-12">
                  {/* Introduction */}
                  <div className="mb-12 p-6 bg-blue-50 border-l-4 border-blue-400 rounded-r-lg">
                    <p className="text-gray-700 leading-relaxed">
                      At <strong className="text-blue-700">www.formatally.com</strong>, we are committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website and use our services. Please read this policy carefully to understand our practices regarding your personal data.
                    </p>
                  </div>

                  {/* Section 1 */}
                  <section id="information-collection" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">1</span>
                      </div>
                      Information We Collect
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>We may collect several types of information from and about users of our website:</p>
                      
                      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Personal Information</h4>
                          <p>Information that can identify you personally, such as your name, email address, phone number, and any other information you voluntarily provide when contacting us or subscribing to our services.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Usage Information</h4>
                          <p>Information about how you access and use our website, including your IP address, browser type, device information, pages visited, and time spent on our site.</p>
                        </div>
                        
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">Technical Information</h4>
                          <p>Information collected automatically through cookies and similar technologies, including your preferences and settings.</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Section 2 */}
                  <section id="information-use" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">2</span>
                      </div>
                      How We Use Your Information
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>We use the information we collect for various purposes, including:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-green-50 rounded-lg p-4 border-l-4 border-green-400">
                          <h4 className="font-semibold text-green-900 mb-2">Service Provision</h4>
                          <p className="text-green-800 text-sm">To provide, maintain, and improve our services and respond to your inquiries.</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 border-l-4 border-purple-400">
                          <h4 className="font-semibold text-purple-900 mb-2">Communication</h4>
                          <p className="text-purple-800 text-sm">To send you updates, newsletters, and marketing communications (with your consent).</p>
                        </div>
                        <div className="bg-orange-50 rounded-lg p-4 border-l-4 border-orange-400">
                          <h4 className="font-semibold text-orange-900 mb-2">Analytics</h4>
                          <p className="text-orange-800 text-sm">To analyze usage patterns and improve our website's performance and user experience.</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-400">
                          <h4 className="font-semibold text-red-900 mb-2">Legal Compliance</h4>
                          <p className="text-red-800 text-sm">To comply with legal obligations and protect our rights and interests.</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Section 3 */}
                  <section id="information-sharing" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">3</span>
                      </div>
                      Sharing of Information
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <div className="flex items-start">
                          <svg className="w-6 h-6 text-red-600 mt-1 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-red-900 mb-2">We Do Not Sell Your Data</h4>
                            <p className="text-red-800">We do not sell, trade, or rent your personal information to third parties for commercial purposes.</p>
                          </div>
                        </div>
                      </div>
                      
                      <p>We may share your information only in the following limited circumstances:</p>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span><strong>Service Providers:</strong> With trusted third-party service providers who assist us in operating our website and conducting our business.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span><strong>Legal Requirements:</strong> When required by law, court order, or government regulation.</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mt-1 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span><strong>Business Transfers:</strong> In connection with a merger, sale, or transfer of company assets.</span>
                        </li>
                      </ul>
                    </div>
                  </section>

                  {/* Section 4 */}
                  <section id="data-security" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">4</span>
                      </div>
                      Data Security
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                          <svg className="w-12 h-12 text-blue-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                          </svg>
                          <h4 className="font-semibold text-blue-900 mb-2">Encryption</h4>
                          <p className="text-blue-800 text-sm">Data transmission and storage encryption</p>
                        </div>
                        <div className="text-center p-6 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
                          <svg className="w-12 h-12 text-green-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                          <h4 className="font-semibold text-green-900 mb-2">Access Controls</h4>
                          <p className="text-green-800 text-sm">Limited access to authorized personnel only</p>
                        </div>
                        <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg border border-purple-200">
                          <svg className="w-12 h-12 text-purple-600 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          <h4 className="font-semibold text-purple-900 mb-2">Secure Storage</h4>
                          <p className="text-purple-800 text-sm">Protected servers and databases</p>
                        </div>
                      </div>
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-800 text-sm">
                          <strong>Important:</strong> While we strive to protect your personal information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                        </p>
                      </div>
                    </div>
                  </section>

                  {/* Section 5 */}
                  <section id="cookies" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">5</span>
                      </div>
                      Cookies and Tracking Technologies
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>We use cookies and similar tracking technologies to enhance your browsing experience. Cookies are small data files stored on your device that help us:</p>
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Essential Cookies</h4>
                            <ul className="space-y-2 text-sm">
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Remember your preferences
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Ensure website functionality
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-green-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Maintain security
                              </li>
                            </ul>
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 mb-3">Analytics Cookies</h4>
                            <ul className="space-y-2 text-sm">
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Understand site usage
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Improve our services
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                </svg>
                                Track performance metrics
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                      <p className="text-sm">You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.</p>
                    </div>
                  </section>

                  {/* Continue with remaining sections... */}
                  <section id="your-rights" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">10</span>
                      </div>
                      Your Privacy Rights
                    </h2>
                    <div className="space-y-4 text-gray-700 leading-relaxed">
                      <p>Depending on your location, you may have the following rights regarding your personal information:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                          <h4 className="font-semibold text-blue-900 mb-2">Access & Portability</h4>
                          <p className="text-blue-800 text-sm">Request access to and receive a copy of your personal data</p>
                        </div>
                        <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                          <h4 className="font-semibold text-green-900 mb-2">Correction</h4>
                          <p className="text-green-800 text-sm">Request correction of inaccurate or incomplete information</p>
                        </div>
                        <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                          <h4 className="font-semibold text-red-900 mb-2">Deletion</h4>
                          <p className="text-red-800 text-sm">Request deletion of your personal information</p>
                        </div>
                        <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                          <h4 className="font-semibold text-purple-900 mb-2">Opt-out</h4>
                          <p className="text-purple-800 text-sm">Withdraw consent for processing or marketing communications</p>
                        </div>
                      </div>
                      <p>To exercise these rights, please contact us using the information provided in the "Contact Us" section.</p>
                    </div>
                  </section>

                  {/* Contact Section */}
                  <section id="contact" className="mb-10">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
                      <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <span className="text-blue-600 font-semibold text-sm">12</span>
                      </div>
                      Contact Us
                    </h2>
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-8 border border-blue-200">
                      <div className="text-center">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">Questions About This Policy?</h3>
                        <p className="text-gray-600 mb-6">If you have any questions about this Privacy Policy or our privacy practices, please don't hesitate to contact us.</p>
                        <div className="space-y-4">
                          <a
                            href="mailto:contact@formatally.com"
                            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
                          >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            contact@formatally.com
                          </a>
                          <p className="text-sm text-gray-500">We typically respond within 24-48 hours</p>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Footer */}
                  <div className="border-t border-gray-200 pt-8 mt-12">
                    <div className="text-center space-y-4">
                      <div className="flex items-center justify-center space-x-4 text-sm text-gray-500">
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          Effective Date: May 20, 2025
                        </span>
                        <span>•</span>
                        <span className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                          </svg>
                          Last Updated: May 20, 2025
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 max-w-2xl mx-auto">
                        This privacy policy is designed to help you understand how we collect, use, and safeguard your information. 
                        We are committed to maintaining your trust and protecting your privacy.
                      </p>
                      <div className="pt-4">
                        <a
                          href="#top"
                          className="inline-flex items-center text-blue-600 hover:text-blue-700 transition-colors duration-200 text-sm font-medium"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                          </svg>
                          Back to Top
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;